import type { ReactNode } from "react";
import "./Banner.css";

export type BannerTone = "info" | "warning" | "danger";

interface BannerProps {
  tone: BannerTone;
  title: string;
  children?: ReactNode;
  /** A single action, e.g. a link to the page where this can be acted on
   * (kept to one so a banner never turns into a toolbar). */
  action?: ReactNode;
  /** Extra class(es) for placement context — e.g. a modal wants tighter
   * padding/rounded corners than the app-level, full-bleed placement. */
  className?: string;
}

/** `danger` interrupts a screen reader (something may be wrong beyond
 * economics — a tampered/invalid license); `info`/`warning` are announced
 * politely so they don't compete with whatever the operator was doing. */
const ROLE_FOR_TONE: Record<BannerTone, "status" | "alert"> = {
  info: "status",
  warning: "status",
  danger: "alert",
};

/**
 * A persistent, full-width notice — distinct from `ToastContext`'s toasts,
 * which are for transient one-off events. A banner describes an ongoing
 * condition (a license state, a capacity limit) that stays true until the
 * underlying thing changes, so it has no auto-dismiss and no close button:
 * it disappears when the condition it describes does.
 */
export function Banner({ tone, title, children, action, className }: BannerProps) {
  return (
    <div className={["banner", `banner--${tone}`, className].filter(Boolean).join(" ")} role={ROLE_FOR_TONE[tone]}>
      <div className="banner__body">
        <p className="banner__title">{title}</p>
        {children && <div className="banner__description">{children}</div>}
      </div>
      {action && <div className="banner__action">{action}</div>}
    </div>
  );
}
